/**
 * Dastarkhan Permissions Context
 *
 * Provides shared permission state via React Context so all components
 * (GraphToolbar, NodeDetailPanel, EdgeDetailPanel, etc.) see the same
 * project membership data.
 *
 * Usage:
 *   1. Wrap your app with <PermissionsProvider> (inside AuthProvider)
 *   2. Call loadProjectMemberships(projectId) when a project is loaded
 *   3. All descendants can call usePermissions() to check canCreate, canUpdate, etc.
 */

import React, { useState, useCallback, useRef, createContext, useContext } from 'react';
import { useApiData, useAuthenticatedFetch } from '../api/client';
import type { DastarkhanUser, ProjectMembership } from '../api/dastarkhan';

interface PermissionsContextValue {
  user: DastarkhanUser | null;
  isAdmin: boolean;
  isConfigured: boolean;
  loading: boolean;
  canRead: (projectId: string) => boolean;
  canCreate: (projectId: string) => boolean;
  canUpdate: (projectId: string) => boolean;
  canDelete: (projectId: string) => boolean;
  isOwner: (projectId: string) => boolean;
  loadProjectMemberships: (projectId: string) => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, loading, error } = useApiData<DastarkhanUser>('/dastarkhan/users/me');
  const { fetchData } = useAuthenticatedFetch<ProjectMembership[]>();
  const [memberships, setMemberships] = useState<Map<string, ProjectMembership>>(new Map());

  // Use a ref for fetchData so loadProjectMemberships has a stable reference
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  // 503 means dastarkhan module is not configured
  const isConfigured = !error || error.status !== 503;

  const isAdmin = data?.role === 'admin';

  const loadProjectMemberships = useCallback(async (projectId: string) => {
    if (!data?.id) return;
    try {
      const members = await fetchDataRef.current(`/dastarkhan/projects/${projectId}/members`);
      if (members) {
        setMemberships((prev) => {
          const next = new Map(prev);
          const myMembership = members.find((m) => m.user_id === data.id);
          if (myMembership) next.set(projectId, myMembership);
          return next;
        });
      }
    } catch {
      // Permissions default to restricted on failure
    }
  }, [data?.id]);

  const canRead = useCallback(
    (_projectId: string) => isAdmin || true, // All members have read by default
    [isAdmin]
  );

  const canCreate = useCallback(
    (projectId: string) => {
      if (isAdmin) return true;
      const m = memberships.get(projectId);
      return m?.can_create ?? false;
    },
    [isAdmin, memberships]
  );

  const canUpdate = useCallback(
    (projectId: string) => {
      if (isAdmin) return true;
      const m = memberships.get(projectId);
      return m?.can_update ?? false;
    },
    [isAdmin, memberships]
  );

  const canDelete = useCallback(
    (projectId: string) => {
      if (isAdmin) return true;
      const m = memberships.get(projectId);
      return m?.can_delete ?? false;
    },
    [isAdmin, memberships]
  );

  const isOwner = useCallback(
    (_projectId: string) => {
      // Owner check requires project data, resolved at component level
      return false;
    },
    []
  );

  const value: PermissionsContextValue = {
    user: data ?? null,
    isAdmin,
    isConfigured,
    loading,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    isOwner,
    loadProjectMemberships,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
